/*
	ExceptionsIssues.tsx -- Page for viewing and creating exceptions and issues.
*/
import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';

import { DocumentApi } from 'Api/Document/DocumentApi';
import { ExceptionsApi } from 'Api/Exceptions/ExceptionsApi';
import { IssuesApi } from 'Api/Issues/IssuesApi';
import { ClientTPRMApi } from 'Api/TPRM/TPRMApi';
import { ClientUcrmApi } from 'Api/UCRM/UcrmApi';
import { getOrganizations } from 'Api/UcrmTprm/UcrmTprmApi';
import { useCachedData } from 'Components/Context/CachedDataContext';
import { PageLayoutDefault } from 'Components/PageLayout/PageLayoutDefault';
import { Placeholder } from 'Components/Placeholder/Placeholder';
import { PrimaryTabs, Tab } from 'Components/Tabs/PrimaryTabs/PrimaryTabs';
import { Text } from 'Components/Text/Text';
import { GENERIC_ERROR_MESSAGE } from 'Config/Errors';
import { SEARCH_PARAM_ISSUES_EXCEPTIONS_ORGANIZATION_ID } from 'Helpers/URLBuilder/URLBuilder';
import { OrganizationResponseWithAssessmentSubjectsBase } from 'Models/AssessmentSubject';
import { DueDiligenceModule, ProductModule } from 'Models/ProductModule';

import { ExceptionsTab, ExceptionsTabProps } from './Exceptions/Components/ExceptionsTab/ExceptionsTab';
import { IssuesTab, IssuesTabProps } from './Issues/Components/IssuesTab/IssuesTab';

enum ExceptionsIssuesTabKey {
    ISSUES = 'issues',
    EXCEPTIONS = 'exceptions',
}

interface ExceptionsIssuesOperationalControlsProps {
    type: ProductModule.CONTROLS;
    issuesApi: IssuesApi;
    exceptionsApi: ExceptionsApi;
    documentApi: DocumentApi;
    moduleApi?: never; // Typed like this so that `moduleApi` can be used unconditionally as a `useEffect` dependency.
}

interface ExceptionsIssuesDueDiligenceProps {
    type: DueDiligenceModule;
    issuesApi: IssuesApi;
    exceptionsApi: ExceptionsApi;
    documentApi: DocumentApi;
    moduleApi: ClientTPRMApi | ClientUcrmApi;
}

export type ExceptionsIssuesProps = ExceptionsIssuesOperationalControlsProps | ExceptionsIssuesDueDiligenceProps;

/**
 * Renders two tabs: one for issues and one for exceptions.
 */
export const ExceptionsIssues = (props: ExceptionsIssuesProps) => {
    const cachedData = useCachedData();
    const location = useLocation();
    const navigate = useNavigate();
    const query = new URLSearchParams(location.search);
    const organizationIdQueryParam = query.get(SEARCH_PARAM_ISSUES_EXCEPTIONS_ORGANIZATION_ID) ?? undefined;
    const [organizations, setOrganizations] = useState<OrganizationResponseWithAssessmentSubjectsBase[]>(); // Not fetched for Operational Controls issues/exceptions, which use an empty array instead.
    const [pageErrorOccurred, setPageErrorOccurred] = useState(false);

    useEffect(() => {
        const fetchOrganizations = async (): Promise<void> => {
            if (props.type === ProductModule.CONTROLS) {
                setOrganizations([]);
                return;
            }

            try {
                const response = await getOrganizations(props.moduleApi);
                setOrganizations(response.data);
            } catch (error) {
                console.log(error);
                setPageErrorOccurred(true);
            }
        };

        fetchOrganizations();
    }, [props.moduleApi, props.type]);

    /**
     * Determine which tab to load by default.
     */
    const getDefaultTab = (): ExceptionsIssuesTabKey => {
        if (location.hash && location.hash.length > 0) {
            const strippedHash = location.hash.substring(1);
            if (Object.values(ExceptionsIssuesTabKey).includes(strippedHash as ExceptionsIssuesTabKey)) {
                return strippedHash as ExceptionsIssuesTabKey;
            }
        }

        return ExceptionsIssuesTabKey.ISSUES;
    };

    /**
     * Update the URL when the user changes to a new tab. This allows the user to return to the most recently used tab, rather than always landing on the default tab.
     * @param selectedTab comes from the eventKey attribute of the <Tab /> component.
     */
    const handleTabChange = (selectedTab?: string): void => {
        navigate(`${location.pathname}#${selectedTab}`, { replace: true });
    };

    if (pageErrorOccurred) {
        return <Text>{GENERIC_ERROR_MESSAGE}</Text>;
    }

    if (!organizations) {
        return <Placeholder />;
    }

    const issuesTabProps: IssuesTabProps = (() => {
        switch (props.type) {
            case ProductModule.CONTROLS:
                return {
                    issuesType: ProductModule.CONTROLS,
                    issuesApi: props.issuesApi,
                    documentApi: props.documentApi,
                    users: cachedData.users,
                };
            case ProductModule.TPRM:
            case ProductModule.UCRM:
                return {
                    issuesType: props.type,
                    issuesApi: props.issuesApi,
                    documentApi: props.documentApi,
                    users: cachedData.users,
                    organizations: organizations,
                    organizationIdQueryParam: organizationIdQueryParam,
                };
        }
    })();

    const exceptionsTabProps: ExceptionsTabProps = (() => {
        switch (props.type) {
            case ProductModule.CONTROLS:
                return {
                    exceptionsType: ProductModule.CONTROLS,
                    exceptionsApi: props.exceptionsApi,
                    documentApi: props.documentApi,
                    users: cachedData.users,
                };
            case ProductModule.TPRM:
            case ProductModule.UCRM:
                return {
                    exceptionsType: props.type,
                    exceptionsApi: props.exceptionsApi,
                    documentApi: props.documentApi,
                    users: cachedData.users,
                    organizations: organizations,
                    organizationIdQueryParam: organizationIdQueryParam,
                };
        }
    })();

    return (
        <PageLayoutDefault
            headerTitle="Issues and Exceptions"
            body={[
                {
                    content: (
                        <PrimaryTabs defaultActiveTab={getDefaultTab()} onSelect={handleTabChange} removePadding>
                            <Tab title="Issues" eventKey={ExceptionsIssuesTabKey.ISSUES}>
                                <IssuesTab {...issuesTabProps} />
                            </Tab>
                            <Tab title="Exceptions" eventKey={ExceptionsIssuesTabKey.EXCEPTIONS}>
                                <ExceptionsTab {...exceptionsTabProps} />
                            </Tab>
                        </PrimaryTabs>
                    ),
                },
            ]}
        />
    );
};
